import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

type Suggestion = {
  id: string;
  label: string;
};

type MapboxFeature = {
  id: string;
  place_name: string;
  place_type: string[];
  context?: Array<{ id: string; short_code?: string; text: string }>;
  text: string;
};

function formatPlaceName(feature: MapboxFeature): string {
  const ctx = feature.context ?? [];

  const region = ctx.find((c) => c.id.startsWith("region."));
  const country = ctx.find((c) => c.id.startsWith("country."));
  const countryCode = country?.short_code?.toUpperCase();

  const city = feature.text;

  if (countryCode === "US" || countryCode === "CA") {
    const shortRegion = region?.short_code?.replace(/^(us|ca)-/i, "").toUpperCase();
    if (shortRegion) return `${city}, ${shortRegion}`;
  }

  if (country) return `${city}, ${country.text}`;

  return feature.text;
}

export const LocationAutocomplete = ({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = (q: string) => {
    if (!MAPBOX_TOKEN || q.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    const url = new URL(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json`
    );
    url.searchParams.set("types", "place");
    url.searchParams.set("language", "en");
    url.searchParams.set("limit", "6");
    url.searchParams.set("access_token", MAPBOX_TOKEN);

    fetch(url.toString())
      .then((r) => r.json())
      .then((data) => {
        const features = data.features ?? [];
        const results: Suggestion[] = features.map((f: MapboxFeature) => ({
          id: f.id,
          label: formatPlaceName(f),
        }));
        setSuggestions(results);
        setOpen(results.length > 0);
      })
      .catch(() => {
        setSuggestions([]);
        setOpen(false);
      });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    onChange(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(q), 250);
  };

  const handleSelect = (label: string) => {
    setQuery(label);
    onChange(label);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className ?? ""}`}>
      <Input
        value={query}
        onChange={handleChange}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder ?? "City, State or Country"}
        className="h-7 text-sm"
        autoComplete="off"
      />
      {open && (
        <ul className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md text-sm max-h-52 overflow-auto">
          {suggestions.map((s) => (
            <li
              key={s.id}
              className="px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(s.label);
              }}
            >
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
